/**
 * 🐋 Proof Upload Section - Cetalingua FM
 * 
 * For free advertisers to upload proof of distributing stickers/flyers
 * Get 3 free months of advertising for promoting Cetalingua FM
 */

import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './ProofUploadSection.css';
import '../portal/portal-theme.css';

// Sticker program (1 Oct 2026): ask for the free stickers with one click — no email needed.
// The request (mailing address) lands in the admin's "📦 Promo requests" list. Once per account.
const PromoRequest = ({ userData }) => {
  const loc = userData?.location || {};
  const [req, setReq] = useState(userData?.promoRequest || null);
  const [a, setA] = useState({ name: userData?.companyName || '', street: '', street2: '', city: loc.city || '',
                               state: loc.state || '', postalCode: '', country: loc.country || '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setReq(userData?.promoRequest || null); }, [userData]);
  const fmt = (t) => (t?.toDate ? t.toDate().toLocaleDateString() : '');
  const set = (k) => (e) => setA((p) => ({ ...p, [k]: e.target.value }));

  if (req) {
    const ad = req.address || {};
    return (
      <div className="contact-section">
        <h3>📦 Promotional materials</h3>
        <p>{req.status === 'sent'
          ? `✅ Sent${req.sentAt ? ` on ${fmt(req.sentAt)}` : ''} to:`
          : `Requested${req.requestedAt ? ` on ${fmt(req.requestedAt)}` : ''} — we will mail them to:`}</p>
        <div className="ct-address" style={{ display: 'inline-block', textAlign: 'left', minWidth: 260 }}>{[ad.name, ad.street, ad.street2, `${ad.postalCode} ${ad.city}${ad.state ? `, ${ad.state}` : ''}`, ad.country].filter(Boolean).join('\n')}</div>
      </div>
    );
  }
  const send = async () => {
    setBusy(true); setErr('');
    try {
      const r = await httpsCallable(getFunctions(), 'requestPromoMaterials')({ address: a });
      setReq({ status: r.data.status || 'requested', address: a });
    } catch (e) { setErr(e.message || 'Could not send the request.'); }
    setBusy(false);
  };
  return (
    <div className="contact-section">
      <h3>📦 Get your free promotional materials</h3>
      <p>Tell us where to mail the stickers and flyers — free of charge.</p>
      <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'left' }}>
        <input className="ct-input" placeholder="Name / attention" value={a.name} onChange={set('name')} />
        <input className="ct-input" placeholder="Street address *" value={a.street} onChange={set('street')} />
        <input className="ct-input" placeholder="Apt / suite / dock" value={a.street2} onChange={set('street2')} />
        <div className="ct-grid2">
          <input className="ct-input" placeholder="City *" value={a.city} onChange={set('city')} />
          <input className="ct-input" placeholder="State / province" value={a.state} onChange={set('state')} />
          <input className="ct-input" placeholder="ZIP / postal code *" value={a.postalCode} onChange={set('postalCode')} />
          <input className="ct-input" placeholder="Country *" value={a.country} onChange={set('country')} />
        </div>
        {err && <p className="ct-err">{err}</p>}
        <div style={{ textAlign: 'center', marginTop: 6 }}>
          <button className="ct-btn primary" disabled={busy} onClick={send}>{busy ? 'Sending…' : '📦 Request free promo materials'}</button>
        </div>
      </div>
    </div>
  );
};

const ProofUploadSection = ({ userId, userData }) => {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [proofUrl, setProofUrl] = useState(userData?.proofUrl || null);
  const [proofStatus, setProofStatus] = useState(userData?.proofStatus || null);
  const [proofRejectionReason, setProofRejectionReason] = useState(userData?.proofRejectionReason || null);

  // Sync with userData changes
  useEffect(() => {
    if (userData) {
      setProofUrl(userData.proofUrl || null);
      setProofStatus(userData.proofStatus || null);
      setProofRejectionReason(userData.proofRejectionReason || null);
    }
  }, [userData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file (JPG, PNG, etc.)' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be less than 10MB' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      // Upload to Firebase Storage
      const timestamp = Date.now();
      const filename = `proof-uploads/${userId}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, filename);
      
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      // Update user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        proofUrl: downloadUrl,
        proofStatus: 'pending',
        proofUploadedAt: new Date(),
        proofRejectionReason: null,
        updatedAt: new Date(),
      });

      setProofUrl(downloadUrl);
      setProofStatus('pending');
      setProofRejectionReason(null);
      setMessage({ type: 'success', text: 'Proof uploaded successfully! Awaiting admin review.' });

    } catch (error) {
      console.error('Error uploading proof:', error);
      setMessage({ type: 'error', text: 'Failed to upload proof. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const getStatusDisplay = () => {
    switch (proofStatus) {
      case 'pending':
        return (
          <div className="status-display pending">
            <div className="status-icon">⏳</div>
            <div className="status-text">
              <h3>Proof Under Review</h3>
              <p>Our team is reviewing your submission. You'll be notified within 24-48 hours.</p>
            </div>
          </div>
        );
      case 'approved':
        return (
          <div className="status-display approved">
            <div className="status-icon">✅</div>
            <div className="status-text">
              <h3>Proof Approved!</h3>
              <p>Thank you for promoting Cetalingua FM! Your 3-month free subscription is now active.</p>
            </div>
          </div>
        );
      case 'rejected':
        return (
          <div className="status-display rejected">
            <div className="status-icon">❌</div>
            <div className="status-text">
              <h3>Proof Needs Revision</h3>
              <p><strong>Reason:</strong> {proofRejectionReason || 'Please upload a new photo that clearly shows the stickers/flyers distributed.'}</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="proof-upload-section">
      <div className="section-header">
        <h2>🎟️ Free Advertising Program</h2>
        <p>Get 3 months of free advertising by helping promote Cetalingua FM</p>
      </div>

      {/* How It Works */}
      <div className="how-it-works">
        <h3>How It Works</h3>
        <div className="steps">
          <div className="step">
            <div className="step-num">1</div>
            <div className="step-content">
              <h4>Get Stickers/Flyers</h4>
              <p>{!userData?.promoRequest ? 'We mail them to you for free — fill in the address below'
                : userData.promoRequest.status === 'sent' ? '✅ Sent to you — check your mail'
                : '📦 Requested — we are preparing your package'}</p>
            </div>
          </div>
          <div className="step">
            <div className="step-num">2</div>
            <div className="step-content">
              <h4>Distribute</h4>
              <p>Place stickers or flyers at your location, on boats, or share with customers</p>
            </div>
          </div>
          <div className="step">
            <div className="step-num">3</div>
            <div className="step-content">
              <h4>Upload Proof</h4>
              <p>Take a photo showing the materials distributed and upload below</p>
            </div>
          </div>
          <div className="step">
            <div className="step-num">4</div>
            <div className="step-content">
              <h4>Get Free Advertising</h4>
              <p>Once approved, enjoy 3 months of free listings and promotion!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Status Display */}
      {proofStatus && getStatusDisplay()}

      {/* Current Proof */}
      {proofUrl && (
        <div className="current-proof">
          <h3>Your Uploaded Proof</h3>
          <div className="proof-image">
            <img src={proofUrl} alt="Proof of distribution" />
          </div>
          {proofStatus === 'pending' && (
            <p className="proof-note">This proof is awaiting admin review.</p>
          )}
        </div>
      )}

      {/* Upload Section */}
      {(!proofStatus || proofStatus === 'rejected') && (
        <div className="upload-section">
          <h3>{proofStatus === 'rejected' ? 'Upload New Proof' : 'Upload Your Proof'}</h3>
          <p className="upload-instructions">
            Upload a clear photo showing Cetalingua FM stickers or flyers displayed or distributed.
            Acceptable proof includes:
          </p>
          <ul className="proof-examples">
            <li>📍 Stickers placed at your business location</li>
            <li>🚢 Stickers on boats or vehicles</li>
            <li>📋 Flyers displayed in your office or waiting area</li>
            <li>🎁 Materials being given to customers</li>
          </ul>

          <label className="upload-btn">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            {uploading ? (
              <>
                <span className="uploading-spinner"></span>
                Uploading...
              </>
            ) : (
              <>
                📷 {proofStatus === 'rejected' ? 'Upload New Photo' : 'Upload Proof Photo'}
              </>
            )}
          </label>
          <p className="file-note">Max file size: 10MB. Accepted formats: JPG, PNG, HEIC</p>
        </div>
      )}

      {/* Free program used up: once per account */}
      {userData?.stickerProgramUsedAt && !(userData?.products || []).includes('advertiser') && (
        <div className="contact-section">
          <h3>Free program used</h3>
          <p>The 3 free months can be used once per company. To keep advertising, choose a plan under 🏷️ Plans.</p>
        </div>
      )}

      {/* Promotional materials: one-click request, mailed by us */}
      <PromoRequest userData={userData} />
    </div>
  );
};

export default ProofUploadSection;
